import { type ChangeEvent, useCallback, useState } from 'react';
import { type z, type ZodType } from 'zod';

import { getErrorMessage, getFieldErrors } from '@/utils/errors';
import { type FieldErrors, toFieldErrors } from '@/utils/validation';

type FormValues = Record<string, string | boolean>;
type KeysOfType<T, V> = { [K in keyof T]: T[K] extends V ? K : never }[keyof T] & string;

type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/**
 * Small form state helper — values, field errors and a form-level error — so forms need no
 * library. Validate with a Zod schema on submit (`parse`), and pass a failed mutation's error to
 * `handleError`: a 422 lands on the matching fields, anything else becomes `formError`.
 */
export function useForm<T extends FormValues>(initialValues: T) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const setValue = useCallback(<K extends keyof T & string>(name: K, value: T[K]) => {
    setValues((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => {
      if (!(name in previous)) return previous;
      const { [name]: _cleared, ...rest } = previous;
      return rest;
    });
  }, []);

  /**
   * Props for a text-like input, select or textarea bound to `name`.
   *
   * Pass `checkWith` (a field-level schema, e.g. `mobileInputSchema`) to flag a wrong value as soon
   * as the field loses focus, instead of only on submit. An empty field is left alone — a missing
   * required value is reported on submit, so tabbing through the form stays quiet.
   */
  function field(name: KeysOfType<T, string>, checkWith?: ZodType) {
    return {
      name,
      value: values[name] as string,
      error: errors[name],
      onChange: (event: ChangeEvent<FieldElement>) => {
        setValue(name, event.target.value as T[typeof name]);
      },
      ...(checkWith
        ? {
            onBlur: () => {
              const text = values[name] as string;
              if (text.trim() === '') return;
              const result = checkWith.safeParse(text);
              if (!result.success) {
                const message = result.error.issues[0]?.message ?? 'Check this value';
                setErrors((previous) => ({ ...previous, [name]: message }));
              }
            },
          }
        : {}),
    };
  }

  /** Props for a checkbox bound to `name`. */
  function checkbox(name: KeysOfType<T, boolean>) {
    return {
      name,
      checked: values[name] as boolean,
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        setValue(name, event.target.checked as T[typeof name]);
      },
    };
  }

  /** Validates the current values; returns the parsed output, or null after showing errors. */
  function parse<S extends ZodType>(schema: S): z.output<S> | null {
    const result = schema.safeParse(values);
    if (result.success) {
      setErrors({});
      setFormError(null);
      return result.data;
    }
    setErrors(toFieldErrors(result.error));
    setFormError(null);
    return null;
  }

  /** Shows a failed request: field errors on their fields, everything else as `formError`. */
  function handleError(error: unknown) {
    const fieldErrors = getFieldErrors(error);
    const onFields: FieldErrors = {};
    const elsewhere: string[] = [];
    for (const [key, message] of Object.entries(fieldErrors)) {
      if (key in values) onFields[key] = message;
      else elsewhere.push(message);
    }
    setErrors(onFields);
    if (elsewhere.length > 0) setFormError(elsewhere.join(' '));
    else if (Object.keys(onFields).length > 0) setFormError(null);
    else setFormError(getErrorMessage(error));
  }

  function reset(next: T = initialValues) {
    setValues(next);
    setErrors({});
    setFormError(null);
  }

  return {
    values,
    errors,
    formError,
    setValue,
    setFormError,
    field,
    checkbox,
    parse,
    handleError,
    reset,
  };
}
